import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { MenuLinkItem } from '@rossum/components/menu-link-item/MenuLinkItem';
import {
  IconCode,
  IconDeviceTv,
  IconHelpCircle,
  IconInfoSquare,
  IconKeyboard,
  IconLifebuoy,
  IconLink,
  IconMessage,
  IconRocket,
} from '@rossum/ui/icons/tabler';
import { IconButton, Menu, Stack, SvgIcon } from '@rossum/ui/material';
import {
  DEVELOPER_HUB_LINK,
  HELP_CENTER_LINK,
  KEYBOARD_SHORTCUTS_LINK,
  QUICK_START_GUIDE,
  ROSSUM_VIDEOS_LINK,
} from '../../constants/values';
import {
  openFreshchat,
  reportProblem,
} from '../../redux/modules/report/actions';
import { userRoleNameSelector } from '../../redux/modules/user/selectors';
import { SupportMenuItem } from '../../types/organization';
import { useIsFeatureEnabled } from '../feature-enablement/featureEnablement';

type StaticMenuItem = {
  key:
    | 'quickStart'
    | 'videos'
    | 'helpCenter'
    | 'devHub'
    | 'keyboardShortcuts'
    | 'report'
    | 'chat';
  icon: React.ComponentType;
  href: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  dataCy?: string;
  rel?: string;
};

type Props = {
  supportMenuItems?: SupportMenuItem[] | null;
};

export const HelpPanel = ({ supportMenuItems }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const dispatch = useDispatch();
  const intl = useIntl();

  const isFreshchatEnabled = useIsFeatureEnabled('mat-1113-freshdesk-chat');

  const userRole = useSelector(userRoleNameSelector);
  const isManagerOrAdmin = [
    'admin',
    'organization_group_admin',
    'manager',
    'empowered_manager',
  ].includes(userRole);

  // Chat is enabled only for admins&managers since we do not have more granularity in feature flags
  const isChatEnabled = isManagerOrAdmin && isFreshchatEnabled;

  const isOpen = !!anchorEl;

  const hideSupportMenu = supportMenuItems && supportMenuItems.length === 0;

  if (hideSupportMenu) {
    return null;
  }

  const staticMenuItems: Array<StaticMenuItem> = [
    {
      key: 'quickStart',
      icon: IconRocket,
      href: QUICK_START_GUIDE,
      rel: 'noreferrer noopener',
    },
    {
      key: 'videos',
      icon: IconDeviceTv,
      href: ROSSUM_VIDEOS_LINK,
      rel: 'noreferrer noopener',
    },
    {
      key: 'helpCenter',
      icon: IconLifebuoy,
      href: HELP_CENTER_LINK,
      rel: 'noreferrer noopener',
    },
    {
      key: 'devHub',
      icon: IconCode,
      href: DEVELOPER_HUB_LINK,
      rel: 'noreferrer noopener',
    },
    {
      key: 'keyboardShortcuts',
      icon: IconKeyboard,
      href: KEYBOARD_SHORTCUTS_LINK,
      rel: 'noreferrer noopener',
    },
    {
      key: 'report',
      icon: IconInfoSquare,
      href: '#',
      dataCy: 'report-problem-button',
      onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        dispatch(reportProblem());
      },
    },
    ...(isChatEnabled
      ? [
          {
            key: 'chat' as const,
            icon: IconMessage,
            href: '#',
            dataCy: 'open-chat-button',
            onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
              e.preventDefault();
              dispatch(openFreshchat());
            },
          },
        ]
      : []),
  ] as const;

  return (
    <Stack direction="row" spacing={1}>
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        {supportMenuItems
          ? supportMenuItems.map(({ url, label }) => (
              <MenuLinkItem
                key={url + label}
                href={url}
                Icon={IconLink}
                label={label}
                onClick={undefined}
                rel="noopener noreferrer"
                external
              />
            ))
          : staticMenuItems.map(item => (
              <MenuLinkItem
                key={item.key}
                href={item.href}
                Icon={item.icon}
                label={intl.formatMessage({
                  id: `components.helpPanel.${item.key}`,
                })}
                onClick={item.onClick}
                dataCy={item.dataCy}
                rel={item.rel}
                external
              />
            ))}
      </Menu>
      <IconButton
        onClick={e => setAnchorEl(e.currentTarget)}
        color="secondary"
        data-cy="help-panel-button"
      >
        <SvgIcon fontSize="medium">
          <IconHelpCircle />
        </SvgIcon>
      </IconButton>
    </Stack>
  );
};
